import { Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Logo, Wordmark, Star } from "@/components/landing/Logo";
import { useLang } from "@/i18n/LanguageContext";

const KEY_VISUAL = "https://static.prod-images.emergentagent.com/jobs/78bcda24-ecc7-4097-99a8-74d5dca5b1b3/images/304e5cd3f917aa5c937a3cf46f8510d6db6577811ecfea635ba83938dd88ddb9.jpeg";
const PRESS_KIT_ZIP = `${process.env.REACT_APP_BACKEND_URL}/api/press-kit`;

const COLORS = [
  { name: "Creme", hex: "#FCFAF8" },
  { name: "Gold", hex: "#D4AF37" },
  { name: "Camel", hex: "#C69C6D" },
  { name: "Rosé", hex: "#DDA7A5" },
  { name: "Rosé Soft", hex: "#EAD5D5" },
  { name: "Nachtbraun", hex: "#2C2725" },
];

export default function Press() {
  const { t } = useLang();
  const p = t.press;

  return (
    <main style={{ background: "var(--surface)", color: "var(--on-surface)" }} className="min-h-screen">
      <Navbar />
      <div className="mx-auto max-w-4xl px-5 sm:px-8 pt-36 pb-24" data-testid="press-page">
        <Link to="/" data-testid="press-back-link" className="inline-flex items-center gap-2 text-sm font-medium transition-colors duration-300 hover:text-[var(--camel)]" style={{ color: "var(--muted-warm)" }}>
          <ArrowLeft size={15} /> {t.legal.back}
        </Link>

        <p className="mt-10 flex items-center gap-2 text-xs uppercase font-medium" style={{ color: "var(--camel)", letterSpacing: "0.16em" }}>
          <Star size={11} /> {p.kicker}
        </p>
        <h1 className="mt-5 font-serif-display text-4xl sm:text-5xl font-semibold leading-tight" data-testid="press-title">
          {p.title}
        </h1>
        <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed" style={{ color: "var(--muted-warm)" }}>
          {p.intro}
        </p>

        <div className="mt-12 overflow-hidden rounded-3xl border" style={{ borderColor: "var(--gold)" }} data-testid="press-key-visual">
          <img src={KEY_VISUAL} alt="Mira Mirror Key Visual" className="w-full" />
          <div className="flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }}>
            <span className="text-sm" style={{ color: "var(--muted-warm)" }}>{p.keyVisual}</span>
            <div className="flex flex-wrap items-center gap-2">
              <a
                href={KEY_VISUAL}
                download="mira-mirror-key-visual.jpeg"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="press-download-btn"
                className="inline-flex items-center gap-2 rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 hover:border-[var(--camel)] hover:text-[var(--camel)]"
                style={{ borderColor: "var(--line)" }}
              >
                <Download size={15} /> {p.download}
              </a>
              <a
                href={PRESS_KIT_ZIP}
                data-testid="press-zip-btn"
                className="pill-btn inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-white"
                style={{ background: "var(--camel)" }}
              >
                <Download size={15} /> {p.zip}
              </a>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-3xl border px-6 py-5" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }} data-testid="press-release-section">
          <p className="font-serif-display text-xl font-semibold">{p.prTitle}</p>
          <p className="mt-1 text-sm leading-relaxed" style={{ color: "var(--muted-warm)" }}>{p.prText}</p>
          <ul className="mt-5 space-y-3">
            {[
              { date: "11/2026", tag: "B2C", title: "Mira Mirror jetzt im App Store & bei Google Play", file: "/downloads/mira-pressemitteilung-app-launch.pdf", tid: "press-release-applaunch" },
              { date: "11/2026", tag: "B2C", title: "Neuer Schrankmodus: Der eigene Kleiderschrank wird zum Stylisten", file: "/downloads/mira-pressemitteilung-schrankmodus.pdf", tid: "press-release-schrank" },
              { date: "11/2026", tag: "B2B", title: "Schaufenster-Modus: Mannequin-Looks per Tap an der eigenen Figur", file: "/downloads/mira-pressemitteilung-schaufenster-modus.pdf", tid: "press-release-schaufenster" },
              { date: "06/2026", tag: "B2B", title: "Der KI-Zauberspiegel für Boutiquen (Launch)", file: "/downloads/mira-pressemitteilung-boutiquen.pdf", en: "/downloads/mira-press-release-boutiques-en.pdf", tid: "press-release-de" },
            ].map((r) => (
              <li key={r.tid} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3" style={{ borderColor: "var(--line)", background: "var(--surface)" }}>
                <div className="flex items-center gap-3">
                  <span className="rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase" style={{ borderColor: "var(--camel)", color: "var(--camel)", letterSpacing: "0.08em" }}>{r.tag}</span>
                  <div>
                    <p className="text-sm font-medium">{r.title}</p>
                    <p className="text-xs" style={{ color: "var(--muted-warm)" }}>{r.date}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a href={r.file} target="_blank" rel="noopener noreferrer" data-testid={`${r.tid}-btn`}
                    className="pill-btn inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium text-white" style={{ background: "var(--camel)" }}>
                    <Download size={13} /> {p.prDe}
                  </a>
                  {r.en && (
                    <a href={r.en} target="_blank" rel="noopener noreferrer" data-testid="press-release-en-btn"
                      className="inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition-colors duration-300 hover:border-[var(--camel)] hover:text-[var(--camel)]" style={{ borderColor: "var(--line)" }}>
                      <Download size={13} /> {p.prEn}
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <section className="mt-16">
          <h2 className="font-serif-display text-3xl font-semibold">{p.factsTitle}</h2>
          <ul className="mt-6 space-y-3.5">
            {p.facts.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-sm sm:text-base leading-relaxed">
                <span className="mt-0.5 shrink-0" style={{ color: "var(--gold)" }}>✦</span>
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-14">
          <h2 className="font-serif-display text-3xl font-semibold">{p.storyTitle}</h2>
          <p className="mt-5 text-sm sm:text-base leading-relaxed" style={{ color: "var(--muted-warm)" }}>{p.story}</p>
        </section>

        <section className="mt-14" data-testid="press-brand-section">
          <h2 className="font-serif-display text-3xl font-semibold">{p.brandTitle}</h2>

          <div className="mt-7 rounded-3xl border p-8 text-center" style={{ borderColor: "var(--line)", background: "var(--surface-secondary)" }}>
            <div className="flex justify-center"><Logo size={64} /></div>
            <div className="mt-4"><Wordmark className="text-xl" /></div>
            <p className="mt-2 text-xs" style={{ color: "var(--muted-warm)" }}>{t.footer.claim}</p>
            <p className="mx-auto mt-5 max-w-md text-xs leading-relaxed" style={{ color: "var(--muted-warm)" }}>
              <b>{p.logoTitle}:</b> {p.logoText}
            </p>
          </div>

          <h3 className="mt-10 font-serif-display text-2xl font-semibold">{p.colorsTitle}</h3>
          <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3" data-testid="press-colors">
            {COLORS.map((c) => (
              <div key={c.hex} className="overflow-hidden rounded-2xl border" style={{ borderColor: "var(--line)" }}>
                <div className="h-16" style={{ background: c.hex }} />
                <div className="px-4 py-2.5">
                  <p className="text-sm font-medium">{c.name}</p>
                  <p className="text-xs uppercase" style={{ color: "var(--muted-warm)" }}>{c.hex}</p>
                </div>
              </div>
            ))}
          </div>

          <h3 className="mt-10 font-serif-display text-2xl font-semibold">{p.fontsTitle}</h3>
          <p className="mt-3 text-sm sm:text-base" style={{ color: "var(--muted-warm)" }}>{p.fontsText}</p>
          <p className="mt-4 font-serif-display text-3xl">Cormorant Garamond – Aa Bb Cc</p>
          <p className="mt-1 text-lg">DM Sans – Aa Bb Cc</p>
        </section>

        <section className="mt-14 rounded-3xl border p-8" style={{ borderColor: "var(--gold)" }} data-testid="press-contact">
          <h2 className="font-serif-display text-2xl font-semibold">{p.contactTitle}</h2>
          <p className="mt-3 text-sm sm:text-base" style={{ color: "var(--camel)" }}>{p.contactText}</p>
          <p className="mt-2 font-serif-display text-lg italic" style={{ color: "var(--camel)" }}>{t.social.hashtags}</p>
        </section>
      </div>
      <Footer />
    </main>
  );
}
